
import { useState } from "react";
import { useSettings } from "@/providers/SettingsProvider";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue
} from "@/components/ui/select";
import {
  DATE_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  TIMEZONE_OPTIONS,
  useWorkingHours,
  setWorkingHours
} from "@/utils/settings";
import { Slider } from "@/components/ui/slider";

export const DateTimeFormatSettings = () => {
  const { 
    dateFormat, setDateFormatValue, 
    timeFormat, setTimeFormatValue,
    timezone, setTimezoneValue
  } = useSettings();
  const [previewDate] = useState(new Date());
  const { startHour, endHour } = useWorkingHours();
  const [localStartHour, setLocalStartHour] = useState(startHour);
  const [localEndHour, setLocalEndHour] = useState(endHour);
  
  // Get the selected date format
  const selectedDateFormat = DATE_FORMAT_OPTIONS.find(opt => opt.id === dateFormat);
  
  // Get the selected time format
  const selectedTimeFormat = TIME_FORMAT_OPTIONS.find(opt => opt.id === timeFormat);

  // Get the selected timezone
  const selectedTimezone = TIMEZONE_OPTIONS.find(opt => opt.id === timezone) || TIMEZONE_OPTIONS[0];
  
  // Preview the combined date and time
  const getFormattedPreview = () => {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: selectedDateFormat?.format.includes('MMM') ? 'short' : '2-digit',
      day: '2-digit',
      hour: selectedTimeFormat?.format.includes('HH') ? '2-digit' : 'numeric',
      minute: '2-digit',
      second: selectedTimeFormat?.format.includes('ss') ? '2-digit' : undefined,
      hour12: selectedTimeFormat?.format.includes('a'),
      timeZone: selectedTimezone?.id
    });
    
    return dateFormatter.format(previewDate);
  };

  // Handle working hours change
  const handleStartHourChange = (value: number[]) => {
    const newStartHour = value[0];
    setLocalStartHour(newStartHour);
    if (newStartHour >= localEndHour) {
      setLocalEndHour(Math.min(newStartHour + 1, 23));
    }
    setWorkingHours(newStartHour, localEndHour);
  };

  const handleEndHourChange = (value: number[]) => {
    const newEndHour = value[0];
    setLocalEndHour(newEndHour);
    if (newEndHour <= localStartHour) {
      setLocalStartHour(Math.max(newEndHour - 1, 0));
    }
    setWorkingHours(localStartHour, newEndHour);
  };
  
  // Format hour display
  const formatHour = (hour: number): string => {
    if (selectedTimeFormat?.format.includes('HH')) {
      return `${hour.toString().padStart(2, '0')}:00`;
    }
    const period = hour < 12 ? 'AM' : 'PM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };
  
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date Format
          </label>
          <Select value={dateFormat} onValueChange={setDateFormatValue}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select date format" />
            </SelectTrigger>
            <SelectContent>
              {DATE_FORMAT_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  <span className="flex justify-between w-full">
                    <span>{option.name}</span>
                    <span className="text-gray-500">{option.example}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Time Format
          </label>
          <Select value={timeFormat} onValueChange={setTimeFormatValue}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select time format" />
            </SelectTrigger>
            <SelectContent>
              {TIME_FORMAT_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  <span className="flex justify-between w-full">
                    <span>{option.name}</span>
                    <span className="text-gray-500">{option.example}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Timezone
          </label>
          <Select value={timezone} onValueChange={setTimezoneValue}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {TIMEZONE_OPTIONS.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  <span className="flex justify-between w-full">
                    <span>{option.name}</span>
                    <span className="text-gray-500">GMT{option.offset}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Preview:</p>
          <p className="text-sm font-medium">{getFormattedPreview()}</p>
        </div>
      </div>
      
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Working Hours (Calendar)
        </h4>
        
        <div className="space-y-6">
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Start Time
              </label>
              <span className="text-sm font-medium">
                {formatHour(localStartHour)}
              </span>
            </div>
            <Slider 
              value={[localStartHour]} 
              min={0} 
              max={23} 
              step={1}
              onValueChange={handleStartHourChange}
            />
          </div>
          
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                End Time
              </label>
              <span className="text-sm font-medium">
                {formatHour(localEndHour)}
              </span>
            </div>
            <Slider 
              value={[localEndHour]} 
              min={0} 
              max={23} 
              step={1}
              onValueChange={handleEndHourChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
